import {
  Button,
  Card,
  CircularLoader,
  Popover,
  Select,
  Stack,
  Title,
  useTheme,
  useToast
} from "@introist/react-foundation/v2";
import { listTimezones } from "../../../utils/dates";
import { useCallback, useState } from "react";
import { isEqual } from "lodash";

export type Schedule = {
  days: number[];
  hours: number[];
  timezone: string;
};

const Days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
const Hours = [
  "0",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "11",
  "12",
  "13",
  "14",
  "15",
  "16",
  "17",
  "18",
  "19",
  "20",
  "21",
  "22",
  "23"
] as const;

export const ScheduleManager = ({
  initialSchedule,
  onSave
}: {
  initialSchedule: Schedule;
  onSave: (schedule: Schedule) => Promise<unknown>;
}) => {
  const toast = useToast();

  const [open, setOpen] = useState(false);

  const [schedule, setSchedule] = useState(initialSchedule);
  const [saving, setSaving] = useState(false);

  const [ref, setRef] = useState<HTMLDivElement | null>(null);

  const close = useCallback(async () => {
    setOpen(false);

    if (!isEqual(schedule, initialSchedule)) {
      setSaving(true);
      await onSave(schedule)
        .then(() => {
          toast.success("Schedule updated");
        })
        .catch(() => toast.error("Failed to update schedule"))
        .finally(() => setSaving(false));
    }
  }, [onSave, schedule, initialSchedule, toast]);

  return (
    <div ref={setRef}>
      <Button
        variant="blended"
        onClick={() => {
          if (open) close();
          else setOpen(true);
        }}
        endAdornment={saving && <CircularLoader size="small" />}
      >
        <Title>
          Runs {initialSchedule.days.length} days a week, {initialSchedule.hours.length} times a day
        </Title>
      </Button>
      <Popover referenceElement={ref} open={open} onClose={close} closeOnContentClick={false}>
        <ScheduleEditor schedule={schedule} onChange={setSchedule} title="Schedule" />
      </Popover>
    </div>
  );
};

export const ScheduleEditor = ({
  title,
  schedule,
  onChange
}: {
  title?: string;
  schedule: Schedule;
  onChange: (schedule: Schedule) => unknown;
}) => {
  const { theme } = useTheme();

  const toggleDay = (day: number) => () => {
    const newDays = [...schedule.days];
    if (newDays.includes(day)) {
      newDays.splice(newDays.indexOf(day), 1);
    } else {
      newDays.push(day);
    }
    onChange({ ...schedule, days: newDays.sort() });
  };

  return (
    <Card elevated>
      <Stack vertical>
        {title && <Title variant="bold">{title}</Title>}
        <Stack gap="xSmall">
          {Days.map((day, index) => {
            const selected = schedule.days.includes(index + 1);
            return (
              <Button
                variant={selected ? "outlined" : "blended"}
                key={day}
                onClick={toggleDay(index + 1)}
                size="small"
              >
                <Title
                  variant="bold"
                  color={
                    selected ? theme.palette.foreground.default : theme.palette.foreground.dimmed
                  }
                >
                  {day}
                </Title>
              </Button>
            );
          })}
        </Stack>
        <Stack gap="xSmall" style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)" }}>
          {Hours.map((hour, index) => {
            const selected = schedule.hours.includes(index);
            return (
              <Button
                variant={selected ? "outlined" : "blended"}
                key={hour}
                onClick={() => {
                  const newHours = [...schedule.hours];
                  if (newHours.includes(index)) {
                    newHours.splice(newHours.indexOf(index), 1);
                  } else {
                    newHours.push(index);
                  }
                  onChange({ ...schedule, hours: newHours.sort() });
                }}
                size="small"
              >
                <Title
                  variant="bold"
                  color={
                    selected ? theme.palette.foreground.default : theme.palette.foreground.dimmed
                  }
                >
                  {hour}
                </Title>
              </Button>
            );
          })}
        </Stack>

        <Stack>
          <Title>Timezone</Title>
          <Select
            searchable
            size="small"
            style={{ width: "100%" }}
            variant="outlined"
            element="button"
            options={listTimezones().map(timezone => ({ key: timezone, label: timezone }))}
            value={schedule.timezone}
            onSelect={opt => Promise.resolve(onChange({ ...schedule, timezone: opt.key }))}
          />
        </Stack>
      </Stack>
    </Card>
  );
};
